import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { cn } from "../../../lib/utils";
import { AlertCircle, Copy, CheckCheck } from "lucide-react";
import { Alert, AlertDescription } from "../../ui/alert";
import { useMediaQuery } from "../../../hooks/use-mobile";
import React, { useState } from "react";
import { Button } from "../../ui/button";
import { useToast } from "../../ui/use-toast";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface TranscriptSectionProps {
  meetingId: string;
}

interface SpeakerSegment {
  speaker: string;
  text: string;
  start: number;
  end: number;
  confidence?: number;
  words?: Array<{
    text: string;
    start: number;
    end: number;
    confidence: number;
    speaker: string;
  }>;
}

function formatTimestamp(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function TranscriptSection({ meetingId }: TranscriptSectionProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const { data: transcriptData, isLoading, error } = useQuery({
    queryKey: ['transcript', meetingId],
    queryFn: async () => {
      console.log('Fetching transcript for meeting:', meetingId);
      const { data, error } = await supabase
        .from('transcriptions')
        .select('transcription, raw_response')
        .eq('meeting_id', meetingId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Transcript fetch error:', error);
        throw error;
      }

      if (!data) {
        return {
          text: '',
          speakers: []
        };
      }

      let speakers: SpeakerSegment[] = [];
      if (data?.raw_response) {
        try {
          const response = JSON.parse(data.raw_response);
          if (response.utterances) {
            speakers = response.utterances.map((u: any) => ({
              speaker: `Speaker ${u.speaker}`,
              text: u.text,
              start: u.start,
              end: u.end,
              confidence: u.confidence,
              words: u.words
            }));
          }
        } catch (e) {
          console.error('Error parsing raw response:', e);
        }
      }

      return {
        text: data?.transcription || '',
        speakers
      };
    },
  });

  const copyTranscript = () => {
    if (!transcriptData) return;
    
    let textToCopy = '';
    
    if (transcriptData.speakers.length > 0) {
      textToCopy = transcriptData.speakers.map(segment => 
        `${segment.speaker} (${formatTimestamp(segment.start)}): ${segment.text}`
      ).join('\n\n');
    } else {
      textToCopy = transcriptData.text;
    }
    
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        setCopied(true);
        toast({
          title: "Success",
          description: "Transcript copied to clipboard",
        });
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        toast({
          title: "Error",
          description: "Failed to copy transcript",
          variant: "destructive",
        });
      });
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error instanceof Error ? error.message : 'Failed to load transcript'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!transcriptData) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Transcript</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full shadow-md"
                onClick={copyTranscript}
              >
                {copied ? <CheckCheck className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy transcript</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div className="relative">
        {transcriptData.speakers.length > 0 ? (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-4">
            {transcriptData.speakers.map((segment, index) => (
              <div key={index} className={cn(
                "flex gap-4 text-sm",
                isMobile && "flex-col"
              )}>
                <div className={cn(
                  "shrink-0",
                  !isMobile && "space-y-1 w-32",
                  isMobile && "flex items-center gap-2"
                )}>
                  <span className="text-gray-500">{formatTimestamp(segment.start)}</span>
                  {isMobile ? (
                    <div className="font-medium text-primary">{segment.speaker}</div>
                  ) : (
                    <div className="font-medium text-primary">{segment.speaker}</div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-foreground leading-relaxed">{segment.text}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={cn(
            "prose prose-sm max-w-none dark:prose-invert max-h-[60vh] overflow-y-auto",
            "prose-p:leading-7",
            "prose-ul:list-disc prose-ul:pl-6",
          )}>
            {transcriptData.text || "No transcript available"}
          </div>
        )}
      </div>
    </div>
  );
}
